/**
 * Leads — `docs/API.md` §4.
 *
 * **Mostly implemented server-side now.** List, filter-tree query, create, read,
 * patch, delete, restore, duplicate-detection, assign and bulk-assign are live
 * in `salescrmapi/src/modules/leads`. Convert, rescore, clone, merge and
 * timeline still answer `501 not_implemented` naming their section, which is
 * what `withMockFallback` keys on — so those, and only those, still serve
 * fixture data.
 *
 * `owner_id` is not patchable — `PATCH /leads/{id}` omits it deliberately:
 * "Assignment is its own permission, its own audit action and usually notifies
 * someone, so it does not belong buried in a field save." Ownership changes go
 * through `assignLead`/`bulkAssignLeads` below, which call the real
 * `POST /leads/{id}/assign` and `POST /leads/bulk-assign` endpoints —
 * `leads:assign`, held only by admin and manager in the seeded roles, is
 * enforced server-side independent of whatever the UI chooses to show.
 *
 * Naming note: `docs/structure.md` suggests `deals.api.js`, but `docs/API.md`
 * and the database call the entity an **opportunity** and keep leads separate.
 * The contract wins — a lead that has not been converted is not a deal.
 */
import apiClient from './axiosClient.js';
import { withMockFallback, paginate, applyFilters } from './mockFallback.js';
import { leads } from './mock/leads.fixture.js';

/**
 * A page of leads.
 *
 * @param {{limit?:number, cursor?:string, status?:string, rating?:string,
 *          owner?:string, source?:string, q?:string, score_gte?:number}} params
 * @returns {Promise<{data:object[], page:object}>}
 */
export function listLeads(params = {}) {
  return withMockFallback(
    'GET /leads',
    async () => (await apiClient.get('/leads', { params })).data,
    /* The fixture is flat (`name`, `company`, `status` as a string) where the
       API nests (`full_name`, `company_name`, `status.name`). `toRow()` below is
       what lets one screen render either — the fixture path is now only reached
       if §4 is somehow unmounted, so it is a safety net, not the normal case. */
    () => {
      let rows = applyFilters(leads, params, {
        search: ['name', 'company', 'email', 'id'],
        equals: ['status', 'rating', 'source', 'owner'],
      });
      if (params.score_gte) rows = rows.filter((l) => l.score >= Number(params.score_gte));
      return paginate(rows, params);
    },
  );
}

/**
 * One lead, for the record page.
 *
 * `include=score_breakdown,notes,similar` is `docs/API.md` §4's full detail
 * grant — `activities`, `attachments` and `history` are the other three
 * `include` values it documents, and all three answer 501 (§8/§11/§12 are not
 * built), so asking for them would turn a working page into an error.
 *
 * @param {string} id
 */
export function getLead(id) {
  return withMockFallback(
    `GET /leads/${id}`,
    async () => (await apiClient.get(`/leads/${id}`, {
      params: { include: 'score_breakdown,notes,similar' },
    })).data,
    () => leads.find((l) => l.id === id) ?? leads[0],
  );
}

/**
 * Create a lead. Carries an Idempotency-Key so a double-submit cannot create two.
 * @param {object} payload
 */
export function createLead(payload) {
  return withMockFallback(
    'POST /leads',
    async () => (await apiClient.post('/leads', payload, { headers: { 'Idempotency-Key': crypto.randomUUID() } })).data,
    () => ({ ...payload, id: `LD-${Math.floor(Math.random() * 90000 + 10000)}` }),
  );
}

/**
 * Update a lead under optimistic locking (§1.7).
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export function updateLead(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /leads/${id}`,
    async () =>
      (await apiClient.patch(`/leads/${id}`, patch, { headers: { 'If-Match': `"${rowVersion}"` } })).data,
    () => ({ ...leads.find((l) => l.id === id), ...patch }),
  );
}

/**
 * Convert a lead into an account, a contact and optionally an opportunity.
 *
 * One call, not three, because the three records have to appear together or not
 * at all — a half-converted lead leaves an account nobody owns and a lead
 * nobody will work again. Answers `409 already_converted` with the ids if it has
 * already happened.
 *
 * @param {string} id
 * @param {{create_opportunity?:boolean, opportunity_name?:string, amount?:number}} options
 */
export function convertLead(id, options = {}) {
  return withMockFallback(
    `POST /leads/${id}/convert`,
    async () => (await apiClient.post(`/leads/${id}/convert`, options)).data,
    () => ({
      lead: { id, status: 'Converted', converted_at: new Date().toISOString() },
      account: { id: 'AC-0041', account_no: 'AC-0041', name: 'Sample account', created: true },
      contact: { id: 'CT-002', contact_no: 'CT-002', full_name: 'Sample contact', job_title: null, created: true },
      opportunity: options.opportunity?.mode === 'skip' ? null : { id: 'OP-0231', opportunity_no: 'OP-0231', name: 'Sample opportunity', created: true },
      moved: { activities: 0 },
    }),
  );
}

/**
 * Soft-delete a lead. `reason` is mandatory, not encouraged.
 *
 * `iam.app_operation.requires_reason` is true for `delete`, so the database
 * demands it; the route validates it first so the caller gets a 422 naming the
 * field rather than a constraint error from somewhere deeper.
 *
 * @param {string} id
 * @param {{reason:string, rowVersion?:number}} options
 */
export function deleteLead(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /leads/${id}`,
    async () =>
      (await apiClient.delete(`/leads/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** Undo a soft delete. @param {string} id */
export function restoreLead(id) {
  return withMockFallback(
    `POST /leads/${id}/restore`,
    async () => (await apiClient.post(`/leads/${id}/restore`)).data,
    () => ({ id, deleted: false }),
  );
}

/**
 * Apply the same patch to several leads.
 *
 * **N requests, sequentially, because there is no bulk endpoint** —
 * `POST /leads/bulk-assign` is 501 and nothing else accepts a list of ids.
 * Sequential rather than parallel on purpose: each one is an audited write
 * against a remote database, and forty concurrent PATCHes exhaust the pool and
 * make the failure mode "some unknown subset applied" instead of "the first
 * eleven applied".
 *
 * Returns every outcome rather than throwing on the first failure. A bulk action
 * that stops halfway and reports only the error leaves the user unable to tell
 * which rows moved — §1 calls this partial success and specifies it for the bulk
 * endpoints; this is the same contract, implemented client-side.
 *
 * @param {Array<{id:string, row_version:number}>} rows
 * @param {object} patch
 * @returns {Promise<{ok:string[], failed:Array<{id:string, message:string}>}>}
 */
export async function patchLeadsBulk(rows, patch) {
  const ok = [];
  const failed = [];
  for (const row of rows) {
    try {
      await updateLead(row.id, patch, row.row_version);
      ok.push(row.id);
    } catch (error) {
      failed.push({ id: row.id, message: error.message });
    }
  }
  return { ok, failed };
}

/**
 * Delete several leads, one at a time, with one shared reason.
 * Same partial-success contract as `patchLeadsBulk`.
 *
 * @param {Array<{id:string, row_version:number}>} rows
 * @param {string} reason
 */
export async function deleteLeadsBulk(rows, reason) {
  const ok = [];
  const failed = [];
  for (const row of rows) {
    try {
      await deleteLead(row.id, { reason, rowVersion: row.row_version });
      ok.push(row.id);
    } catch (error) {
      failed.push({ id: row.id, message: error.message });
    }
  }
  return { ok, failed };
}

/**
 * Change ownership on one lead. `leads:assign` — admin and manager in the
 * seeded roles — is required server-side; a caller without it gets a real 403,
 * not a UI that quietly does nothing.
 *
 * @param {string} id
 * @param {{ownerId:string, reason?:string}} options
 */
export function assignLead(id, { ownerId, reason } = {}) {
  return withMockFallback(
    `POST /leads/${id}/assign`,
    async () => (await apiClient.post(`/leads/${id}/assign`, {
      owner_id: ownerId,
      ...(reason ? { reason } : {}),
    })).data,
    () => ({
      id,
      previous_owner: null,
      owner: { id: ownerId, full_name: 'New owner' },
      assigned_at: new Date().toISOString(),
      notification_sent: false,
    }),
  );
}

/**
 * Change ownership on several leads at once, in one request.
 *
 * Per-record results, partial success by design (§4) — a manager's selection
 * can span leads outside their own scope. Returns `{ok, failed}` in the same
 * shape `patchLeadsBulk`/`deleteLeadsBulk` already use, so the bulk-mutation
 * hook and its toast can treat every bulk action identically regardless of
 * whether the server did it in one round trip or the client did it in N.
 *
 * @param {string[]} ids
 * @param {{ownerId:string, reason?:string}} options
 * @returns {Promise<{ok:string[], failed:Array<{id:string, message:string}>}>}
 */
export async function bulkAssignLeads(ids, { ownerId, reason } = {}) {
  const { results } = await withMockFallback(
    'POST /leads/bulk-assign',
    async () => (await apiClient.post('/leads/bulk-assign', {
      lead_ids: ids,
      owner_id: ownerId,
      ...(reason ? { reason } : {}),
    })).data,
    () => ({ results: ids.map((id) => ({ id, ok: true })) }),
  );
  return {
    ok: results.filter((r) => r.ok).map((r) => r.id),
    failed: results.filter((r) => !r.ok).map((r) => ({ id: r.id, message: r.message ?? r.code })),
  };
}
